import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { getImageUrl } from '../utils/imageUrl';
import { formatPrice } from '../utils/format';
import { Icon, ICON_DEFAULTS } from '../utils/icons';
import './CartSidebar.css';

const CartSidebar = () => {
  const { t, i18n } = useTranslation();
  const { cart, isCartOpen, setIsCartOpen, removeFromCart, updateQuantity, getCartTotal, getCartCount, siteSettings } = useCart();
  const { minOrderAmount, currency: ctxCurrency, currencyLabel } = useSiteSettings();
  const currency = currencyLabel || ctxCurrency || t('common.currency');

  if (!isCartOpen) return null;

  const cartTotal = parseFloat(getCartTotal()) || 0;
  const minOrder = parseFloat(minOrderAmount) || 0;
  const fmtP = (v) => formatPrice(v, { lang: i18n.language });
  const cartCount = getCartCount();

  return (
    <>
      <div className="cart-overlay" onClick={() => setIsCartOpen(false)} />

      <aside className="cart-sidebar" role="dialog" aria-label={t('cart.title')}>
        <header className="cart-header">
          <h2 className="cart-header-title">
            {t('cart.title')}
            {cartCount > 0 && <span className="cart-count-badge">{cartCount}</span>}
          </h2>
          <button
            type="button"
            className="cart-close-btn"
            onClick={() => setIsCartOpen(false)}
            aria-label={t('common.close', 'Close')}
          >
            <Icon.Close size={ICON_DEFAULTS.size} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />
          </button>
        </header>

        <div className="cart-items-container">
          {cart.length === 0 ? (
            <div className="cart-empty">
              <p className="cart-empty-message">{t('cart.empty')}</p>
              <Link to="/products" className="btn btn-primary" onClick={() => setIsCartOpen(false)}>
                {t('cart.shopNow')}
              </Link>
            </div>
          ) : (
            cart.map((item) => {
              const key = item.cartKey || String(item.id);
              const lineTotal = parseFloat(item.price) * item.quantity;
              return (
                <div key={key} className="cart-item">
                  <div className="cart-item-image">
                    {item.image ? (
                      <img
                        src={getImageUrl(item.image, { size: 'thumb' })}
                        alt={item.name}
                        loading="lazy"
                        onError={(e) => {
                          if (!e.target.dataset.fallback) {
                            e.target.dataset.fallback = '1';
                            e.target.src = getImageUrl(item.image);
                          }
                        }}
                      />
                    ) : (
                      <div className="cart-item-image-placeholder" />
                    )}
                  </div>

                  <div className="cart-item-details">
                    <h4 className="cart-item-name">{item.name}</h4>
                    {item.selectedOptions && (
                      <p className="cart-item-meta">
                        {Object.entries(item.selectedOptions).map(([k, v]) => `${k}: ${v}`).join(', ')}
                      </p>
                    )}
                    {item.sku && <p className="cart-item-meta">SKU: {item.sku}</p>}
                    <p className="cart-item-price">{fmtP(item.price)} {currency}</p>

                    <div className="cart-item-quantity">
                      <button
                        type="button"
                        className="quantity-btn"
                        onClick={() => updateQuantity(key, item.quantity - 1)}
                        aria-label={t('cart.decreaseQuantity', 'Decrease quantity')}
                      >
                        <Icon.Minus size={14} strokeWidth={2} aria-hidden="true" />
                      </button>
                      <span className="quantity-value">{item.quantity}</span>
                      <button
                        type="button"
                        className="quantity-btn"
                        onClick={() => updateQuantity(key, item.quantity + 1)}
                        aria-label={t('cart.increaseQuantity', 'Increase quantity')}
                      >
                        <Icon.Plus size={14} strokeWidth={2} aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  <div className="cart-item-actions">
                    <p className="cart-item-subtotal">{fmtP(lineTotal)} {currency}</p>
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => removeFromCart(key)}
                      aria-label={t('cart.removeItem', 'Remove item')}
                    >
                      <Icon.Trash size={16} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {cart.length > 0 && (
          <div className="cart-footer">
            {(() => {
              const sub = parseFloat(getCartTotal());
              const threshold = parseFloat(siteSettings.freeShippingThreshold) || 0;
              if (threshold <= 0) return null;
              const isFree = sub >= threshold;
              const remaining = !isFree ? threshold - sub : 0;
              const progress = Math.min((sub / threshold) * 100, 100);
              return (
                <div className={`cart-free-shipping-banner ${isFree ? 'is-qualified' : ''}`}>
                  <div className="free-shipping-progress-track">
                    <div className="free-shipping-progress-fill" style={{ width: `${progress}%` }} />
                  </div>
                  <div className="free-shipping-text">
                    {isFree
                      ? t('common.freeShippingQualified')
                      : t('common.freeShippingRemaining', { amount: fmtP(remaining), currency })}
                  </div>
                </div>
              );
            })()}

            <div className="cart-total">
              <span>{t('common.subtotal')}</span>
              <span className="cart-total-amount">{fmtP(getCartTotal())} {currency}</span>
            </div>

            {minOrder > 0 && cartTotal < minOrder && (
              <div className="cart-min-order-warning">
                {t('common.minOrderRemaining', { amount: fmtP(minOrder - cartTotal), currency })}
              </div>
            )}

            <Link
              to="/checkout"
              className="btn btn-primary btn-block"
              onClick={() => setIsCartOpen(false)}
            >
              {t('cart.checkout')}
            </Link>
            <button
              type="button"
              className="btn btn-secondary btn-block"
              onClick={() => setIsCartOpen(false)}
            >
              {t('cart.continueShopping', t('nav.products'))}
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default CartSidebar;
